/**
 * Reviewed data and deterministic model for the Materials Chemistry tool
 * (study-chemistry-tools-prd.md §8.3, topic "Water & materials", start Year 11).
 *
 * Modes: metallic bonding (`metals`), alloys and heat treatment (`alloys`), metal extraction by
 * reactivity (`extraction`, in materialsChemistryExtraction.ts) and nanomaterials (`nano`, in
 * materialsChemistryNano.ts). Every answer is reviewed data; nothing is generated.
 */
import { checkNumericAnswer, formatSig, type AnswerCheck } from './gasLaws';
import { EXTRACTION_CASES } from './materialsChemistryExtraction';
import { NANO_CASES } from './materialsChemistryNano';

export type MaterialsMode = 'metals' | 'alloys' | 'extraction' | 'nano';

/** Stable identity used by reviewed CurriculumToolBinding records. */
export const MATERIALS_CHEMISTRY_TOOL_IDENTITY = {
  id: 'materials-chemistry',
  toolKey: 'chemistry-materials-chemistry',
  toolVersion: '1.0.0',
  lifecycle: 'live',
  computation: 'client-deterministic',
  evidencePolicy: 'none',
  masteryPolicy: 'not-assessed',
  curriculumPolicy: 'reviewed-binding-required',
} as const;

export const MATERIALS_CHEMISTRY_START_YEAR = 11;
export const MATERIALS_REVIEW_STATUS = 'pending-teaching-review' as const;

export const MATERIALS_MODES: readonly MaterialsMode[] = ['metals', 'alloys', 'extraction', 'nano'];

/**
 * All four modes sit in the same Year 11 unit (§4.5). Metallic bonding underpins the other
 * three, so it is the default for every Year; the learner can switch at any time.
 */
export const MATERIALS_MODE_YEARS: readonly { mode: MaterialsMode; fromYear: number }[] = [
  { mode: 'metals', fromYear: MATERIALS_CHEMISTRY_START_YEAR },
];

export const MATERIALS_SOURCES = [
  {
    label: 'CRC Handbook of Chemistry and Physics',
    detail: 'Melting points of Li, Na, K, Mg, Al and Fe at 101.325 kPa.',
  },
  {
    label: 'Shannon (1976) ionic radii; Greenwood & Earnshaw metallic radii',
    detail:
      'Na⁺ 102, Mg²⁺ 72, Al³⁺ 53 pm (6-coordinate); metallic radii Fe 126, Cr 128, Ni 124, Cu 128, Zn 134, Ag 144, Au 144 pm; covalent radius C 77 pm.',
  },
  {
    label: 'Reactivity series consistent with IUPAC standard electrode potentials',
    detail: 'K > Na > Ca > Mg > Al > (C) > Zn > Fe > Sn > Pb > Cu > Hg > Au.',
  },
  {
    label: 'ISO/TS 80004 and IUPAC nanoscale definition',
    detail: 'Nanoscale: about 1 nm to 100 nm.',
  },
] as const;

export const MATERIALS_CONDITIONS =
  'Melting points are at 101.325 kPa. Radii are tabulated reference values, drawn to scale only within each diagram.';

export type LatticeMetal = 'Na' | 'Mg' | 'Al';

export interface LatticeMetalData {
  symbol: LatticeMetal;
  name: string;
  /** Delocalised electrons contributed per atom. */
  valenceElectrons: number;
  ionCharge: string;
  ionicRadiusPm: number;
  meltingPointC: number;
}

export const LATTICE_METALS: Record<LatticeMetal, LatticeMetalData> = {
  Na: { symbol: 'Na', name: 'Sodium', valenceElectrons: 1, ionCharge: '+', ionicRadiusPm: 102, meltingPointC: 97.8 },
  Mg: { symbol: 'Mg', name: 'Magnesium', valenceElectrons: 2, ionCharge: '2+', ionicRadiusPm: 72, meltingPointC: 650 },
  Al: { symbol: 'Al', name: 'Aluminium', valenceElectrons: 3, ionCharge: '3+', ionicRadiusPm: 53, meltingPointC: 660.3 },
};

/** Melting points at 101.325 kPa, °C. */
export const METAL_MELTING_POINTS: Record<string, { name: string; meltingPointC: number }> = {
  Li: { name: 'Lithium', meltingPointC: 180.5 },
  Na: { name: 'Sodium', meltingPointC: 97.8 },
  K: { name: 'Potassium', meltingPointC: 63.5 },
  Mg: { name: 'Magnesium', meltingPointC: 650 },
  Al: { name: 'Aluminium', meltingPointC: 660.3 },
  Fe: { name: 'Iron', meltingPointC: 1538 },
};

export type AlloyKind = 'substitutional' | 'interstitial';

export interface AlloyComponent {
  symbol: string;
  kind: AlloyKind;
  /** Metallic radius (covalent for C) in pm; omitted when no reviewed value is recorded. */
  radiusPm?: number;
}

export interface AlloyData {
  id: string;
  name: string;
  host: string;
  hostRadiusPm?: number;
  composition: string;
  additives: AlloyComponent[];
}

export const ALLOYS: Record<string, AlloyData> = {
  brass: { id: 'brass', name: 'Brass', host: 'Cu', hostRadiusPm: 128, composition: 'about 60–70 % Cu, rest Zn', additives: [{ symbol: 'Zn', kind: 'substitutional', radiusPm: 134 }] },
  steel: { id: 'steel', name: 'Carbon steel', host: 'Fe', hostRadiusPm: 126, composition: 'Fe with up to about 2 % C', additives: [{ symbol: 'C', kind: 'interstitial', radiusPm: 77 }] },
  bronze: { id: 'bronze', name: 'Bronze', host: 'Cu', hostRadiusPm: 128, composition: 'about 88 % Cu, 12 % Sn', additives: [{ symbol: 'Sn', kind: 'substitutional' }] },
  'gold-18ct': { id: 'gold-18ct', name: '18-carat gold', host: 'Au', hostRadiusPm: 144, composition: '75 % Au, rest Ag and Cu', additives: [{ symbol: 'Ag', kind: 'substitutional', radiusPm: 144 }, { symbol: 'Cu', kind: 'substitutional', radiusPm: 128 }] },
  stainless: { id: 'stainless', name: 'Stainless steel', host: 'Fe', hostRadiusPm: 126, composition: 'Fe with about 18 % Cr, 8 % Ni and a little C', additives: [{ symbol: 'Cr', kind: 'substitutional', radiusPm: 128 }, { symbol: 'Ni', kind: 'substitutional', radiusPm: 124 }, { symbol: 'C', kind: 'interstitial', radiusPm: 77 }] },
  sterling: { id: 'sterling', name: 'Sterling silver', host: 'Ag', hostRadiusPm: 144, composition: '92.5 % Ag, 7.5 % Cu', additives: [{ symbol: 'Cu', kind: 'substitutional', radiusPm: 128 }] },
};

/** Hume-Rothery guide: substitutional solid solutions need radii within about 15 %. */
export const SUBSTITUTIONAL_RADIUS_LIMIT_PERCENT = 15;

/** Percentage difference of an additive radius from the host radius, or null when unknown. */
export function radiusDifferencePercent(alloy: AlloyData, component: AlloyComponent): number | null {
  if (alloy.hostRadiusPm === undefined || component.radiusPm === undefined) return null;
  return (Math.abs(component.radiusPm - alloy.hostRadiusPm) / alloy.hostRadiusPm) * 100;
}

export type HeatProcess = 'quench' | 'temper' | 'anneal';

export interface HeatProfile {
  process: HeatProcess;
  label: string;
  /** Schematic points: time 0–10 and temperature 0–1 (no real scale). */
  points: [number, number][];
  outcome: string;
}

export const HEAT_PROFILES: Record<HeatProcess, HeatProfile> = {
  quench: { process: 'quench', label: 'Quenching', points: [[0, 0.1], [2, 0.9], [4, 0.9], [4.4, 0.1], [10, 0.1]], outcome: 'Very hard, but brittle' },
  temper: { process: 'temper', label: 'Quench, then temper', points: [[0, 0.1], [2, 0.9], [3.5, 0.9], [3.9, 0.1], [5, 0.1], [6, 0.45], [7.5, 0.45], [9, 0.1], [10, 0.1]], outcome: 'Tougher and less brittle; keeps much of the hardness' },
  anneal: { process: 'anneal', label: 'Annealing', points: [[0, 0.1], [2, 0.9], [4, 0.9], [10, 0.15]], outcome: 'Softer and more ductile; easier to shape' },
};

export type MaterialsVisual =
  | { type: 'lattice'; metal: LatticeMetal; shifted: boolean; compareIonic?: boolean }
  | { type: 'melting'; metals: string[] }
  | { type: 'alloy'; alloyId: string }
  | { type: 'heat'; process: HeatProcess }
  | { type: 'ladder'; metal: string }
  | { type: 'size-scale'; sizeNm: number; label: string }
  | { type: 'subdivision'; perEdge: number; caption: string };

export interface MaterialsOption {
  id: string;
  label: string;
  correct: boolean;
  feedback: string;
}

interface MaterialsCaseBase {
  id: string;
  title: string;
  prompt: string;
  visual: MaterialsVisual;
  takeaway: string;
  equation?: string;
}

export interface MaterialsChoiceCase extends MaterialsCaseBase {
  kind: 'choice';
  options: MaterialsOption[];
}

export interface MaterialsNumericCase extends MaterialsCaseBase {
  kind: 'numeric';
  answerLabel: string;
  unit: string;
  answer: number;
  working: string[];
  misconceptions: { value: number; feedback: string }[];
}

export type MaterialsCase = MaterialsChoiceCase | MaterialsNumericCase;

const ELECTRON_SEA_OPTIONS = {
  electrons: 'Delocalised electrons move freely through the lattice',
  ions: 'The metal cations move through the lattice',
  covalent: 'Electrons are shared in fixed covalent bonds between pairs of atoms',
  protons: 'Protons move from atom to atom',
};

export const METALS_CASES: MaterialsChoiceCase[] = [
  {
    id: 'metal-conductivity',
    kind: 'choice',
    title: 'Electrical conductivity',
    prompt: 'Solid sodium conducts electricity. What carries the charge?',
    visual: { type: 'lattice', metal: 'Na', shifted: false },
    options: [
      { id: 'electrons', label: ELECTRON_SEA_OPTIONS.electrons, correct: true, feedback: 'Yes. Each Na atom releases its 3s electron into a sea of delocalised electrons. A voltage makes these electrons drift through the lattice.' },
      { id: 'ions', label: ELECTRON_SEA_OPTIONS.ions, correct: false, feedback: 'The Na⁺ cations stay at fixed lattice positions in a solid. Moving ions carry charge in molten or dissolved ionic compounds, not in solid metals.' },
      { id: 'covalent', label: ELECTRON_SEA_OPTIONS.covalent, correct: false, feedback: 'Electrons in fixed covalent bonds cannot move through a solid. In metals the bonding electrons are delocalised over the whole lattice.' },
      { id: 'protons', label: ELECTRON_SEA_OPTIONS.protons, correct: false, feedback: 'Protons are locked in the nuclei. Only electrons move in a metallic conductor.' },
    ],
    takeaway: 'Metallic bonding is the electrostatic attraction between a lattice of cations and a sea of delocalised electrons.',
  },
  {
    id: 'metal-malleability',
    kind: 'choice',
    title: 'Hammering a metal',
    prompt: 'Magnesium can be hammered into a thin sheet without shattering. Which explanation fits the metallic bonding model?',
    visual: { type: 'lattice', metal: 'Mg', shifted: true },
    options: [
      { id: 'slide', label: 'Layers of cations slide over each other while the electron sea keeps holding them together', correct: true, feedback: 'Yes. Metallic bonding is non-directional: after a layer slides, every cation is still surrounded by delocalised electrons, so the bonding is not broken.' },
      { id: 'weak', label: 'Metallic bonds are weak, so the metal is soft', correct: false, feedback: 'Magnesium melts at 650 °C, so its metallic bonding is strong. Malleability comes from bonding that is non-directional, not weak.' },
      { id: 'covalent', label: 'Covalent bonds break and re-form as it is hammered', correct: false, feedback: 'Metals have no localised covalent bonds to break. The cations shift position inside the same electron sea.' },
      { id: 'gaps', label: 'Atoms are squashed into the gaps between them', correct: false, feedback: 'Atoms do not change size. Whole layers of cations move relative to each other.' },
    ],
    takeaway: 'Non-directional bonding explains both malleability (hammered into sheets) and ductility (drawn into wires).',
  },
  {
    id: 'metal-thermal',
    kind: 'choice',
    title: 'Hot handle',
    prompt: 'An aluminium saucepan handle soon gets hot. Why do metals conduct heat so well?',
    visual: { type: 'lattice', metal: 'Al', shifted: false },
    options: [
      { id: 'electrons', label: 'Mobile delocalised electrons carry kinetic energy quickly through the metal', correct: true, feedback: 'Yes. Free electrons gain kinetic energy at the hot end and move rapidly through the lattice, passing the energy on. Lattice vibrations help too.' },
      { id: 'expand', label: 'The atoms expand and push heat along', correct: false, feedback: 'Atoms do not expand. Energy is passed along mainly by fast-moving delocalised electrons.' },
      { id: 'dense', label: 'Metals are dense, so heat cannot escape', correct: false, feedback: 'Aluminium is a light metal yet an excellent conductor. The mobile electrons, not density, explain conduction.' },
      { id: 'ions', label: 'The Al³⁺ ions move from the hot end to the cold end', correct: false, feedback: 'The ions only vibrate about fixed positions. They do not travel through the solid.' },
    ],
    takeaway: 'The same mobile electrons explain both electrical and thermal conductivity.',
  },
  {
    id: 'metal-lustre',
    kind: 'choice',
    title: 'Shiny surface',
    prompt: 'Freshly cut metals are shiny (lustrous). Which model feature explains this?',
    visual: { type: 'lattice', metal: 'Al', shifted: false },
    options: [
      { id: 'electrons', label: 'Delocalised electrons absorb and re-emit visible light at the surface', correct: true, feedback: 'Yes. The free electrons can absorb a wide range of light energies and re-emit them, so the surface reflects strongly.' },
      { id: 'cations', label: 'The cations give off their own light', correct: false, feedback: 'Metals at room temperature do not emit their own visible light. They reflect incoming light through their delocalised electrons.' },
      { id: 'smooth', label: 'Metals are just smoother than other solids', correct: false, feedback: 'A smooth glass surface is not lustrous like a metal. The interaction of light with delocalised electrons causes metallic lustre.' },
      { id: 'oxide', label: 'A shiny oxide layer forms', correct: false, feedback: 'An oxide layer usually dulls the surface. That is why the metal is shiny only when freshly cut.' },
    ],
    takeaway: 'Lustre is lost when a metal surface oxidises, which is why sodium quickly dulls in air.',
  },
  {
    id: 'metal-period3-melting',
    kind: 'choice',
    title: 'Na, Mg or Al?',
    prompt: 'Which of sodium, magnesium and aluminium has the highest melting point?',
    visual: { type: 'melting', metals: ['Na', 'Mg', 'Al'] },
    options: [
      { id: 'Al', label: 'Aluminium', correct: true, feedback: 'Yes: Al 660 °C, Mg 650 °C, Na 98 °C. Al releases three electrons per atom and forms a small 3+ ion, so the attraction between cations and electron sea is strongest.' },
      { id: 'Mg', label: 'Magnesium', correct: false, feedback: 'Close: Mg melts at 650 °C, only 10 °C below Al (660 °C). Al contributes three delocalised electrons per atom and has a smaller, more highly charged ion.' },
      { id: 'Na', label: 'Sodium', correct: false, feedback: 'Sodium melts at only 98 °C. It contributes one electron per atom and forms a large 1+ ion, so its metallic bonding is weakest.' },
      { id: 'same', label: 'They are about the same', correct: false, feedback: 'Na melts at 98 °C but Mg and Al melt above 640 °C. More delocalised electrons and smaller, higher-charged ions make the bonding much stronger.' },
    ],
    takeaway: 'Across period 3, more delocalised electrons per atom and higher ion charge strengthen metallic bonding.',
  },
  {
    id: 'metal-group1-melting',
    kind: 'choice',
    title: 'Down group 1',
    prompt: 'Which of lithium, sodium and potassium has the lowest melting point?',
    visual: { type: 'melting', metals: ['Li', 'Na', 'K'] },
    options: [
      { id: 'K', label: 'Potassium', correct: true, feedback: 'Yes: Li 181 °C, Na 98 °C, K 64 °C. Each contributes one electron, but the K⁺ ion is largest, so the electron sea is further from the nucleus charge and attraction is weakest.' },
      { id: 'Li', label: 'Lithium', correct: false, feedback: 'Lithium has the highest melting point of the three (181 °C). Its small Li⁺ ion attracts the electron sea most strongly.' },
      { id: 'Na', label: 'Sodium', correct: false, feedback: 'Sodium (98 °C) is in the middle. Melting points fall down the group as the cations get larger.' },
      { id: 'same', label: 'They are the same — all give one electron', correct: false, feedback: 'Same number of delocalised electrons, but different ion sizes. Larger ions mean weaker attraction, so melting points fall from Li to K.' },
    ],
    takeaway: 'Down a group, larger cations hold the delocalised electrons less strongly.',
  },
  {
    id: 'metal-vs-ionic',
    kind: 'choice',
    title: 'Metal bends, salt shatters',
    prompt: 'A hammer blow flattens a magnesium strip but shatters a sodium chloride crystal. Why?',
    visual: { type: 'lattice', metal: 'Mg', shifted: true, compareIonic: true },
    options: [
      { id: 'repulsion', label: 'In NaCl, a shifted layer brings like charges side by side and they repel; in Mg the electron sea still surrounds every cation', correct: true, feedback: 'Yes. Ionic bonding depends on alternating + and − ions. Shift a layer and like charges meet, so the crystal splits. Metallic bonding survives the shift.' },
      { id: 'weaker', label: 'Ionic bonds are weaker than metallic bonds', correct: false, feedback: 'NaCl melts at 801 °C, so its ionic bonding is strong. It is brittle because the bonding depends on ion positions, not because it is weak.' },
      { id: 'electrons-ionic', label: 'NaCl has delocalised electrons that escape', correct: false, feedback: 'Ionic solids have no delocalised electrons. Electrons are transferred to form Na⁺ and Cl⁻ ions held in fixed positions.' },
      { id: 'harder', label: 'Magnesium is harder than salt', correct: false, feedback: 'Hardness does not explain bending versus shattering. The difference is whether the bonding survives when layers slide.' },
    ],
    takeaway: 'Brittleness in ionic solids and malleability in metals both come from how the bonding responds to a shift.',
  },
  {
    id: 'metal-solid-conduction',
    kind: 'choice',
    title: 'Two solids in a circuit',
    prompt: 'Solid magnesium conducts electricity but solid magnesium oxide does not. Why?',
    visual: { type: 'lattice', metal: 'Mg', shifted: false },
    options: [
      { id: 'electrons', label: 'Mg has delocalised electrons; in solid MgO the ions and electrons are held in fixed positions', correct: true, feedback: 'Yes. MgO conducts only when molten, when its ions are free to move. Mg conducts as a solid because of its electron sea.' },
      { id: 'no-ions', label: 'MgO has no charged particles', correct: false, feedback: 'MgO is made of Mg²⁺ and O²⁻ ions. They are charged, but fixed in place in the solid lattice.' },
      { id: 'oxygen', label: 'Oxygen blocks the current', correct: false, feedback: 'The issue is mobility, not a blocking element. No charged particle can move through solid MgO.' },
      { id: 'same', label: 'Both conduct equally', correct: false, feedback: 'Solid MgO is an electrical insulator. Only mobile charged particles conduct.' },
    ],
    takeaway: 'Conduction needs mobile charged particles: electrons in metals, ions in molten or dissolved ionic compounds.',
  },
  {
    id: 'metal-iron-sodium',
    kind: 'choice',
    title: 'Iron versus sodium',
    prompt: 'Iron melts at 1538 °C but sodium at 98 °C. Which explanation fits the model?',
    visual: { type: 'melting', metals: ['Na', 'Fe'] },
    options: [
      { id: 'more-electrons', label: 'Iron contributes more delocalised electrons per atom (including 3d electrons) and has smaller ions', correct: true, feedback: 'Yes. A transition metal like iron releases more electrons into the electron sea and has smaller, higher-charged ions, so its metallic bonding is much stronger.' },
      { id: 'heavier', label: 'Iron atoms are heavier', correct: false, feedback: 'Mass alone does not set melting point. The strength of the attraction between cations and electrons does.' },
      { id: 'covalent', label: 'Iron has covalent bonds as well', correct: false, feedback: 'Pure iron is held together by metallic bonding. It is stronger metallic bonding, not added covalent bonding.' },
      { id: 'magnetic', label: 'Iron is magnetic', correct: false, feedback: 'Magnetism is a separate property. Cobalt and nickel are magnetic too, but melting point depends on bonding strength.' },
    ],
    takeaway: 'Transition metals generally have much higher melting points than group 1 and 2 metals.',
  },
];

const KIND_OPTIONS = (correct: 'substitutional' | 'interstitial' | 'both', feedback: Record<'substitutional' | 'interstitial' | 'both', string>): MaterialsOption[] => [
  { id: 'substitutional', label: 'Substitutional', correct: correct === 'substitutional', feedback: feedback.substitutional },
  { id: 'interstitial', label: 'Interstitial', correct: correct === 'interstitial', feedback: feedback.interstitial },
  { id: 'both', label: 'Both types', correct: correct === 'both', feedback: feedback.both },
];

export const ALLOYS_CASES: MaterialsChoiceCase[] = [
  {
    id: 'alloy-brass',
    kind: 'choice',
    title: 'Brass',
    prompt: 'Brass is copper with zinc. What type of alloy is it?',
    visual: { type: 'alloy', alloyId: 'brass' },
    options: KIND_OPTIONS('substitutional', {
      substitutional: 'Yes. Zn (134 pm) and Cu (128 pm) differ by under 5 %, so zinc atoms replace copper atoms in the lattice.',
      interstitial: 'Zinc atoms are almost as big as copper atoms, so they cannot fit in the small gaps. They take the place of copper atoms instead.',
      both: 'There is no small atom such as carbon in brass. Both metals are similar in size, so only substitution occurs.',
    }),
    takeaway: 'Substitution needs similar atom sizes: within about 15 % of the host radius.',
  },
  {
    id: 'alloy-steel',
    kind: 'choice',
    title: 'Carbon steel',
    prompt: 'Carbon steel is iron with a small amount of carbon. What type of alloy is it?',
    visual: { type: 'alloy', alloyId: 'steel' },
    options: KIND_OPTIONS('interstitial', {
      substitutional: 'Carbon atoms (77 pm) are far smaller than iron atoms (126 pm) — about 39 % smaller — so they do not replace iron atoms.',
      interstitial: 'Yes. The small carbon atoms sit in the gaps (interstices) between iron atoms.',
      both: 'Only two elements are present and carbon fits in the gaps, so carbon steel is interstitial. Stainless steel adds substitutional Cr and Ni.',
    }),
    takeaway: 'Small non-metal atoms such as C, N, B and H form interstitial alloys.',
  },
  {
    id: 'alloy-bronze',
    kind: 'choice',
    title: 'Bronze',
    prompt: 'Bronze is mainly copper with tin. Both are metals with similar-sized atoms. What type of alloy is it?',
    visual: { type: 'alloy', alloyId: 'bronze' },
    options: KIND_OPTIONS('substitutional', {
      substitutional: 'Yes. Tin atoms replace some copper atoms in the lattice. Bronze is harder than pure copper, which is why it was used for tools.',
      interstitial: 'Tin atoms are similar in size to copper atoms, too large for the gaps between them.',
      both: 'No small interstitial atom is present in simple bronze. It is a substitutional alloy.',
    }),
    takeaway: 'The Bronze Age began when people found that copper with tin is much harder than copper alone.',
  },
  {
    id: 'alloy-gold',
    kind: 'choice',
    title: '18-carat gold',
    prompt: '18-carat gold is 75 % gold with silver and copper. What type of alloy is it?',
    visual: { type: 'alloy', alloyId: 'gold-18ct' },
    options: KIND_OPTIONS('substitutional', {
      substitutional: 'Yes. Ag has the same radius as Au (144 pm) and Cu (128 pm) differs by about 11 %, so both replace gold atoms.',
      interstitial: 'Silver and copper atoms are much too big for the gaps between gold atoms.',
      both: 'All three are metals of similar size. Only substitution occurs.',
    }),
    takeaway: 'Pure (24-carat) gold is too soft for everyday jewellery; alloying makes it harder.',
  },
  {
    id: 'alloy-stainless',
    kind: 'choice',
    title: 'Stainless steel',
    prompt: 'Stainless steel contains iron, chromium, nickel and a little carbon. What type of alloy is it?',
    visual: { type: 'alloy', alloyId: 'stainless' },
    options: KIND_OPTIONS('both', {
      substitutional: 'Cr and Ni do substitute for Fe, but the small carbon atoms sit in the gaps. Both types are present.',
      interstitial: 'Carbon is interstitial, but Cr (128 pm) and Ni (124 pm) are almost the size of Fe (126 pm) and replace iron atoms.',
      both: 'Yes. Cr and Ni substitute for Fe; C fills interstitial gaps. Chromium also forms a protective oxide layer that resists rusting.',
    }),
    takeaway: 'One alloy can contain both substitutional and interstitial atoms.',
  },
  {
    id: 'alloy-harder',
    kind: 'choice',
    title: 'Why alloys are harder',
    prompt: 'Brass is harder than pure copper. Use the lattice model to explain why.',
    visual: { type: 'alloy', alloyId: 'brass' },
    options: [
      { id: 'disrupt', label: 'Different-sized atoms disrupt the regular layers, so layers cannot slide as easily', correct: true, feedback: 'Yes. The larger Zn atoms distort the lattice and act like bumps that pin the layers, resisting sliding.' },
      { id: 'stronger-bonds', label: 'Zinc forms covalent bonds with copper', correct: false, feedback: 'Brass is still held by metallic bonding. The change is in the shape of the layers, not a new bond type.' },
      { id: 'fewer-electrons', label: 'The alloy has no delocalised electrons', correct: false, feedback: 'Brass still conducts electricity, so it still has delocalised electrons. Hardness comes from disrupted layers.' },
      { id: 'zinc-hard', label: 'Zinc itself is much harder than copper', correct: false, feedback: 'The alloy can be harder than either pure metal. It is the mixture of atom sizes that matters.' },
    ],
    takeaway: 'Alloys usually conduct slightly less well than the pure metal because the disrupted lattice also scatters electrons.',
  },
  {
    id: 'alloy-sterling',
    kind: 'choice',
    title: 'Sterling silver',
    prompt: 'Why is 7.5 % copper added to silver to make sterling silver for cutlery?',
    visual: { type: 'alloy', alloyId: 'sterling' },
    options: [
      { id: 'harder', label: 'Copper atoms substitute into the silver lattice and make it harder and more durable', correct: true, feedback: 'Yes. Pure silver is soft and dents easily. Substituted Cu atoms (128 pm vs Ag 144 pm) resist layer sliding.' },
      { id: 'shine', label: 'Copper makes the silver shinier', correct: false, feedback: 'Copper is added for strength. In fact the copper makes sterling silver tarnish slightly more easily.' },
      { id: 'cheaper-only', label: 'Only to make it cheaper', correct: false, feedback: 'It is cheaper, but the main purpose is mechanical: pure silver is too soft for cutlery.' },
      { id: 'interstitial', label: 'Copper atoms fill the gaps between silver atoms', correct: false, feedback: 'Copper atoms are too big for the gaps. They replace silver atoms — a substitutional alloy.' },
    ],
    takeaway: 'Choose an alloy for the property you need: hardness, corrosion resistance, melting point or colour.',
  },
  {
    id: 'heat-quench',
    kind: 'choice',
    title: 'Quenching',
    prompt: 'A steel blade is heated until red hot, then plunged into cold water. How do its properties change?',
    visual: { type: 'heat', process: 'quench' },
    options: [
      { id: 'hard-brittle', label: 'It becomes very hard but brittle', correct: true, feedback: 'Yes. Rapid cooling traps the carbon in a strained, distorted structure (martensite) that resists layer sliding — very hard, but it can snap.' },
      { id: 'soft', label: 'It becomes soft and easy to bend', correct: false, feedback: 'That describes annealing (slow cooling). Fast cooling locks in a hard, strained structure.' },
      { id: 'unchanged', label: 'Nothing changes once it cools', correct: false, feedback: 'The cooling rate changes the arrangement of atoms in steel, so the properties do change.' },
      { id: 'rust-proof', label: 'It becomes rust-proof', correct: false, feedback: 'Quenching changes hardness, not corrosion resistance. Stainless steel resists rust because of its chromium.' },
    ],
    takeaway: 'Rate of cooling controls the structure of steel: fast cooling hardens it.',
  },
  {
    id: 'heat-temper',
    kind: 'choice',
    title: 'Tempering',
    prompt: 'A quenched blade is reheated to a moderate temperature (well below red heat) and allowed to cool. Why?',
    visual: { type: 'heat', process: 'temper' },
    options: [
      { id: 'tough', label: 'To reduce brittleness and make it tougher, while keeping much of the hardness', correct: true, feedback: 'Yes. Tempering relieves some internal strain, trading a little hardness for toughness, so tools do not shatter.' },
      { id: 'harder', label: 'To make it even harder', correct: false, feedback: 'Tempering lowers hardness slightly. Its purpose is to reduce brittleness.' },
      { id: 'softest', label: 'To make it as soft as possible', correct: false, feedback: 'That is annealing, which heats to a higher temperature and cools slowly. Tempering keeps much of the hardness.' },
      { id: 'melt', label: 'To melt the surface smooth', correct: false, feedback: 'Tempering temperatures are far below the melting point of steel. The solid structure changes, not the shape.' },
    ],
    takeaway: 'Quench-then-temper gives tools such as chisels and springs both hardness and toughness.',
  },
  {
    id: 'heat-anneal',
    kind: 'choice',
    title: 'Annealing',
    prompt: 'A hardened steel bar is heated to red heat, held there, then left to cool slowly inside the furnace. What is the result?',
    visual: { type: 'heat', process: 'anneal' },
    options: [
      { id: 'soft', label: 'It becomes softer and more ductile, so it is easier to shape', correct: true, feedback: 'Yes. Holding the steel hot and cooling it slowly lets the atoms rearrange into a softer, more regular structure, removing internal strain.' },
      { id: 'hard', label: 'It becomes harder and brittle', correct: false, feedback: 'That describes quenching (fast cooling). Slow cooling from red heat softens steel.' },
      { id: 'alloy', label: 'It turns into a different alloy', correct: false, feedback: 'No element is added or removed, so the composition is unchanged. Only the arrangement of atoms changes.' },
      { id: 'melted', label: 'It melts and re-sets in a new shape', correct: false, feedback: 'Red heat is far below the melting point of steel (about 1400 °C or more). Annealing changes the solid structure, not the shape.' },
    ],
    takeaway: 'Heat treatment changes properties without changing composition.',
  },
];

export const MATERIALS_CASES: Record<MaterialsMode, MaterialsCase[]> = {
  metals: METALS_CASES,
  alloys: ALLOYS_CASES,
  extraction: EXTRACTION_CASES,
  nano: NANO_CASES,
};

export function materialsCaseById(mode: MaterialsMode, id: string): MaterialsCase | undefined {
  return MATERIALS_CASES[mode].find((candidate) => candidate.id === id);
}

export interface MaterialsChoiceResult {
  correct: boolean;
  feedback: string;
}

/** Fails closed on an option id that the case does not offer. */
export function checkMaterialsChoice(item: MaterialsChoiceCase, optionId: string): MaterialsChoiceResult | null {
  const option = item.options.find((candidate) => candidate.id === optionId);
  if (!option) return null;
  return { correct: option.correct, feedback: option.feedback };
}

export function checkMaterialsNumeric(item: MaterialsNumericCase, answer: number): AnswerCheck {
  return checkNumericAnswer(
    answer,
    item.answer,
    item.misconceptions,
    `${item.answerLabel} = ${formatSig(item.answer, 3)} ${item.unit}. ${item.takeaway}`,
    item.unit,
  );
}

export const MATERIALS_BOUNDARIES: Record<MaterialsMode, string[]> = {
  metals: [
    'The electron-sea model is a simplified picture; band theory gives a fuller explanation of conduction and lustre.',
    'Diagrams show a two-dimensional slice. Real metals form three-dimensional close-packed or body-centred lattices.',
  ],
  alloys: [
    'Lattice diagrams are schematic: positions of added atoms are illustrative, not measured.',
    'Heat-treatment graphs are schematic. Real temperatures and times depend on the steel and the tool being made.',
  ],
  extraction: [
    'The reactivity series is a guide. Real extraction routes also depend on ore type, purity needed, energy cost and local industry.',
    'Industrial furnaces and electrolysis cells are hazardous; this tool models chemistry only.',
  ],
  nano: [
    'Surface-area calculations assume perfect cubes or spheres with smooth surfaces.',
    'Nanoparticle safety is assessed case by case; size alone does not make a material safe or unsafe.',
  ],
};
